import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { StripeB2BCheckoutForm } from '../components/StripeB2BCheckoutForm';
import { apiService } from '../services/api';

export const StripeB2BCheckoutPage: React.FC = () => {
  const { planId } = useParams<{ planId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [planDetails, setPlanDetails] = useState<any>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annually'>('monthly');
  const [controllersCount, setControllersCount] = useState(1);
  
  useEffect(() => {
    const fetchPlanDetails = async () => {
      try {
        setLoading(true);
        
        // Get available plans
        const response = await apiService.get('/api/v1/stripe/b2b/plans') as any;
        
        if (response?.success) {
          const plans = response?.data?.plans;
          const selectedPlan = plans.find((p: any) => p.plan_id === planId);
          
          if (selectedPlan) {
            setPlanDetails(selectedPlan);
          } else {
            setError('Plan not found');
          }
        } else {
          setError('Failed to load plans');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load plan details');
      } finally {
        setLoading(false);
      }
    };
    
    if (planId) {
      fetchPlanDetails();
    } else {
      setError('No plan specified');
      setLoading(false);
    }
  }, [planId]);
  
  // Extract query parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const period = searchParams.get('period') as 'monthly' | 'annually';
    const controllers = searchParams.get('controllers');
    
    if (period && ['monthly', 'annually'].includes(period)) {
      setBillingPeriod(period);
    }
    
    if (controllers && !isNaN(Number(controllers))) {
      setControllersCount(Math.max(1, Number(controllers)));
    }
  }, [location.search]);
  
  const handleSuccess = () => {
    // This would be called after successful checkout
    console.log('Checkout successful');
  };
  
  const handleCancel = () => {
    navigate('/plans');
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading checkout...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-semibold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/plans')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Plans
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-semibold text-gray-900">
                Complete Your Subscription
              </h1>
              <button
                onClick={handleCancel}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                ← Back to Plans
              </button>
            </div>
          </div>
          
          <div className="p-6">
            {planDetails && (
              <StripeB2BCheckoutForm
                planId={planId!}
                billingPeriod={billingPeriod}
                controllersCount={controllersCount}
                onSuccess={handleSuccess}
                onCancel={handleCancel}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};